import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { Button } from '@core-ui/Components/ui/button';
import { Input } from '@core-ui/Components/ui/input';

import StatusFilterChips from './StatusFilterChips';
import PreviewContactsTable from './PreviewContactsTable';

import type {
  SessionContactItem,
  SessionContactStatus,
  SessionStatusCounts,
} from '@features/wizard/types/wizard.types';
import Tip from '@ui/shared/Tip.tsx';

interface NewListPreviewProps {
  hasSelection: boolean;
  selectedSessionsCount: number;
  totalFilteredContacts: number;

  counts: SessionStatusCounts;
  activeFilters: SessionContactStatus[];
  onToggleFilter: (status: SessionContactStatus) => void;
  onClearAllFilters: () => void;
  isLoadingCounts: boolean;

  newListName: string;
  onNewListNameChange: (value: string) => void;

  paginatedContacts: SessionContactItem[];
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  isLoadingPreview: boolean;

  canSaveList: boolean;
  isSavingList: boolean;
  savedListId: string | null;
  onSaveList: () => void;
}

const NewListPreview: React.FC<NewListPreviewProps> = ({
  hasSelection,
  selectedSessionsCount,
  totalFilteredContacts,
  counts,
  activeFilters,
  onToggleFilter,
  onClearAllFilters,
  isLoadingCounts,
  newListName,
  onNewListNameChange,
  paginatedContacts,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  isLoadingPreview,
  canSaveList,
  isSavingList,
  savedListId,
  onSaveList,
}) => {
  const { t } = useTranslation();

  if (!hasSelection) {
    return (
      <div className="rounded-xl border border-dashed border-border px-6 py-16 text-center flex flex-col items-center justify-center min-h-[400px]">
        <p className="text-sm font-medium">
          {t('wizard.selectContacts.sessions.previewEmpty.title', 'Select a session')}
        </p>
        <p className="text-xs text-muted-foreground mt-1 max-w-xs">
          {t(
            'wizard.selectContacts.sessions.previewEmpty.subtitle',
            'Pick one or more sessions from the left to preview their contacts and apply filters.',
          )}
        </p>
      </div>
    );
  }

  const fromIndex = (currentPage - 1) * pageSize + 1;
  const toIndex = Math.min(currentPage * pageSize, totalFilteredContacts);

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-end justify-between mb-1.5 gap-3">
          <h3 className="text-lg font-semibold">
            {t('wizard.selectContacts.sessions.previewTitle', 'New contact list preview')}
          </h3>
          <div className="flex items-center gap-3 text-sm flex-wrap justify-end">
            {totalFilteredContacts > 0 && (
              <span className="text-muted-foreground">
                {t('wizard.selectContacts.sessions.showing', {
                  from: fromIndex,
                  to: toIndex,
                  total: totalFilteredContacts,
                  defaultValue: 'Showing {{from}}-{{to}} of {{total}}',
                })}
              </span>
            )}
            {activeFilters.length > 0 && (
              <span className="text-primary font-medium">
                {t('wizard.selectContacts.sessions.activeFilters', {
                  count: activeFilters.length,
                  defaultValue: '{{count}} active filters',
                })}
              </span>
            )}
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          {t('wizard.selectContacts.sessions.previewSubtitle', {
            count: selectedSessionsCount,
            defaultValue:
              'Combining {{count}} sessions. Click chips to filter — the new list will only include the contacts shown below.',
          })}
        </p>
      </div>

      <StatusFilterChips
        counts={counts}
        activeFilters={activeFilters}
        onToggle={onToggleFilter}
        onClearAll={onClearAllFilters}
        isLoading={isLoadingCounts}
      />

      <div className="space-y-1.5">
        <label className="text-xs font-medium text-muted-foreground block">
          {t('wizard.selectContacts.sessions.newListName', 'New list name')}
        </label>
        <div className="flex items-center gap-2">
          <Input
            type="text"
            value={newListName}
            onChange={(e) => {
              onNewListNameChange(e.target.value);
            }}
            placeholder={t(
              'wizard.selectContacts.sessions.namePlaceholder',
              'Enter a name for this filtered list...',
            )}
            disabled={isSavingList || savedListId !== null}
          />
          <Button
            type="button"
            onClick={onSaveList}
            disabled={!canSaveList || isSavingList || savedListId !== null}
            className="whitespace-nowrap"
          >
            {isSavingList ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : savedListId !== null ? (
              t('wizard.selectContacts.sessions.saved', 'Saved')
            ) : (
              t('wizard.selectContacts.sessions.saveList', 'Save list')
            )}
          </Button>
        </div>
        {savedListId !== null && (
          <p className="text-xs text-emerald-500">
            {t('wizard.selectContacts.sessions.savedHint', 'List saved. Click Next to continue.')}
          </p>
        )}
      </div>

      <PreviewContactsTable
        contacts={paginatedContacts}
        totalItems={totalFilteredContacts}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        isLoading={isLoadingPreview}
      />

      <Tip message='wizard.selectContacts.sessions.tip'/>
      
    </div>
  );
};

export default NewListPreview;
